import {
  computed,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  toValue,
  type WritableComputedRef,
} from "vue";

import type { Selection } from "@/types/selection";

const selectAllVisibleItems = <T>(
  items: T[],
  canSelect?: (item: T, selection: Selection<T> | undefined) => boolean
): Selection<T> | null => {
  if (items.length === 0) {
    return null;
  }

  if (!canSelect) {
    return {
      mode: "some",
      items: items as [T, ...T[]],
    };
  }

  let state: Selection<T> | undefined;

  for (const item of items) {
    if (!canSelect(item, state)) {
      continue;
    }

    if (!state) {
      state = { mode: "some", items: [item] };
    } else {
      state.items.push(item);
    }
  }

  return state ?? null;
};

interface UseSelectionCheckboxOptions<T> {
  state: Ref<Selection<T> | undefined>;
  items: MaybeRefOrGetter<T[]>;
  totalCount?: MaybeRefOrGetter<number | undefined>;
  canSelect?: (item: T, selection: Selection<T> | undefined) => boolean;
}

interface UseSelectionCheckboxReturn {
  /** Two-way: read = is everything selected, write(true/false) = select-all / clear. */
  isFullySelected: WritableComputedRef<boolean>;
  isIndeterminate: ComputedRef<boolean>;
}

export const useSelectionCheckbox = <T>(
  options: UseSelectionCheckboxOptions<T>
): UseSelectionCheckboxReturn => {
  const isFullySelected = computed({
    get(): boolean {
      const current = options.state.value;
      if (!current) {
        return false;
      }

      switch (current.mode) {
        case "some":
          return current.items.length === toValue(options.items).length;
        case "all_except":
          return current.items.length === 0;
      }

      return false;
    },
    set(next: boolean) {
      const current = options.state.value;

      if (!current || current.mode === "some") {
        if (next) {
          const result = selectAllVisibleItems(
            toValue(options.items),
            options.canSelect
          );
          if (result !== null) {
            options.state.value = result;
          }
        } else {
          options.state.value = undefined;
        }
      } else {
        options.state.value = next
          ? { mode: "all_except", items: [] }
          : undefined;
      }
    },
  });

  const isIndeterminate = computed((): boolean => {
    const current = options.state.value;
    if (!current || isFullySelected.value) {
      return false;
    }

    switch (current.mode) {
      case "some": {
        const size = current.items.length;
        return size > 0 && size < toValue(options.items).length;
      }
      case "all_except": {
        const size = current.items.length;
        if (size === 0) {
          return false;
        }
        const total = toValue(options.totalCount);
        return total === undefined ? true : size < total;
      }
    }

    return false;
  });

  return { isFullySelected, isIndeterminate };
};
